import classNames from 'classnames';
import {
    ChangeEvent,
    FocusEventHandler,
    InputEvent,
    PointerEventHandler,
    Ref,
    useEffect,
    useRef,
} from 'react';

import { mergeRefs } from '../../lib/utils';

import styles from '../../styles/fields/text-field.module.css';

interface TextareaFieldProps {
    name?: string | null;
    id?: string | null;
    value?: string | null;
    errors?: string[] | null;
    placeholder?: string | null;
    autoComplete?: string | null;
    withoutAutocomplete?: boolean;
    withBorder?: boolean;
    required?: boolean;
    disabled?: boolean;
    maxLength?: number;
    className?: string | null;
    onChange?: ((value: string | null, e: ChangeEvent<HTMLTextAreaElement>) => void) | null;
    onInput?: ((e: InputEvent<HTMLTextAreaElement>) => void) | null;
    onFocus?: FocusEventHandler<HTMLTextAreaElement> | null;
    onBlur?: FocusEventHandler<HTMLTextAreaElement> | null;
    onPointerUp?: PointerEventHandler<HTMLTextAreaElement> | null;
    ref?: Ref<HTMLTextAreaElement> | null;
}

function TextareaField({
    name = null,
    id = null,
    value = null,
    errors = null,
    placeholder = null,
    autoComplete = null,
    withoutAutocomplete = false,
    withBorder = false,
    required = false,
    disabled = false,
    className = null,
    maxLength = null,
    onChange = null,
    onInput: onCustomInput = null,
    onFocus = null,
    onBlur = null,
    onPointerUp = null,
    ref: inputRef = null,
}: TextareaFieldProps) {
    const internalRef = useRef<HTMLTextAreaElement | null>(null);

    const onInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        if (errors !== null) {
            internalRef.current?.setCustomValidity('');
            // internalRef.current.reportValidity();
        }
        const newValue = e.currentTarget.value !== '' ? e.currentTarget.value : null;
        if (onChange !== null) {
            onChange(newValue, e);
        }
    };

    const onInput = (e: InputEvent<HTMLTextAreaElement>) => {
        if (errors !== null) {
            internalRef.current?.setCustomValidity('');
        }
        if (onCustomInput !== null) {
            onCustomInput(e);
        }
    };

    useEffect(() => {
        if (internalRef.current) {
            internalRef.current.setCustomValidity(
                errors !== null && errors.length > 0 ? errors[0] : '',
            );
            if (errors !== null && errors.length > 0) {
                internalRef.current.reportValidity();
            }
        }
    }, [errors]);

    return (
        <textarea
            ref={mergeRefs(internalRef, inputRef)}
            name={name || undefined}
            id={id || undefined}
            value={value || ''}
            placeholder={placeholder || undefined}
            required={required}
            disabled={disabled}
            className={classNames([
                styles.container,
                {
                    [styles.withBorder]: withBorder,
                },
                className,
            ])}
            maxLength={maxLength || undefined}
            onChange={onInputChange}
            onInput={onInput}
            onFocus={onFocus || undefined}
            onBlur={onBlur || undefined}
            onPointerUp={onPointerUp || undefined}
            {...(autoComplete !== null ? { autoComplete } : {})}
            {...(withoutAutocomplete ? { autoComplete: 'off' } : {})}
        />
    );
}

export default TextareaField;
